import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import GoogleAnalyticsRouteListener from './GoogleAnalyticsRouteListener';
import Header from './components/Header';
import Hero from './components/Hero';
import HomeSections from './components/HomeSections';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Features from './components/Features';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import RefundPolicy from './components/RefundPolicy';
import AccountDeletion from './components/AccountDeletion';
import AccessDenied from './components/AccessDenied';
import BookDemoPage from './components/BookDemoPage';
import ScrollToTop from './components/ScrollToTop';
import { useHomeHashScroll } from './hooks/useHomeHashScroll';
import './App.css';
import './mobile.css';
import './styles/iconSizing.css';

const SITE_URL = 'https://pycrust.io';

const ROUTE_METADATA = {
  '/': {
    title: 'Pycrust.io | Run your social media agency. Not chaos.',
    description:
      'Know which draft was approved. Pycrust links workspace assets, drafts, and your pipeline so publishing matches what clients signed off on.',
  },
  '/contact': {
    title: 'Contact | pycrust.io',
    description: 'Contact the pycrust.io team for support, questions, or partnership conversations.',
  },
  '/features': {
    title: 'Features | pycrust.io',
    description:
      'Explore pycrust.io features for workspaces, project libraries, review gates, multi-platform scheduling, analytics, and social inbox.',
  },
  '/book-demo': {
    title: 'Book a demo | pycrust.io',
    description: 'Schedule a demo with the Pycrust team — workspaces, client review, and multi-platform publishing.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | pycrust.io',
    description: 'Read the pycrust.io privacy policy.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | pycrust.io',
    description: 'Read the pycrust.io terms of service.',
  },
  '/refund-policy': {
    title: 'Refund Policy | pycrust.io',
    description: 'Read the pycrust.io refund policy.',
  },
  '/account-deletion': {
    title: 'Account Deletion | pycrust.io',
    description: 'Understand how to request account deletion and what data is removed.',
  },
  '/access-denied': {
    title: 'Access Denied | pycrust.io',
    description: 'Access denied information for pycrust.io.',
  },
};

function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const metadata = ROUTE_METADATA[pathname] || ROUTE_METADATA['/'];
    const canonicalUrl = `${SITE_URL}${pathname}`;

    document.title = metadata.title;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', metadata.description);
    }

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', canonicalUrl);
    }

    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) {
      ogTitleTag.setAttribute('content', metadata.title);
    }

    const ogDescriptionTag = document.querySelector('meta[property="og:description"]');
    if (ogDescriptionTag) {
      ogDescriptionTag.setAttribute('content', metadata.description);
    }

    const ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (ogUrlTag) {
      ogUrlTag.setAttribute('content', canonicalUrl);
    }
  }, [location.pathname]);

  return null;
}

function HomePage() {
  useHomeHashScroll();

  return (
    <div className="App App--home">
      <Header />
      <main>
        <Hero />
        <HomeSections />
      </main>
      <Footer />
    </div>
  );
}

function ContactPage() {
  return (
    <div className="App App--page">
      <Header />
      <main>
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function FeaturesPage() {
  return (
    <div className="App App--page">
      <Header />
      <main>
        <Features />
      </main>
      <Footer />
    </div>
  );
}

function BookDemoRoutePage() {
  return (
    <div className="App App--page">
      <Header />
      <main>
        <BookDemoPage />
      </main>
      <Footer />
    </div>
  );
}

function SitePageShell({ children }) {
  return (
    <div className="App App--page">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <GoogleAnalyticsRouteListener />
      <ScrollToTop />
      <RouteMetadata />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/book-demo" element={<BookDemoRoutePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<Navigate to="/" replace />} />
        <Route path="/data-and-privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/usage-guide" element={<Navigate to="/" replace />} />
        <Route path="/help/notification-permissions" element={<Navigate to="/contact" replace />} />
        <Route path="/privacy-policy" element={<SitePageShell><PrivacyPolicy /></SitePageShell>} />
        <Route path="/terms-of-service" element={<SitePageShell><TermsOfService /></SitePageShell>} />
        <Route path="/refund-policy" element={<SitePageShell><RefundPolicy /></SitePageShell>} />
        <Route path="/account-deletion" element={<SitePageShell><AccountDeletion /></SitePageShell>} />
        <Route path="/access-denied" element={<><Header /><main><AccessDenied /></main><Footer /></>} />
      </Routes>
    </Router>
  );
}

export default App;
